import React, { useEffect } from 'react';
import { X, CheckCircle2, Clock, Calendar } from 'lucide-react';

const ServiceDetailModal = ({ isOpen, onClose, service, onBook }) => {
    // Prevent scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('modal-open');
        } else {
            document.body.style.overflow = 'unset';
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.style.overflow = 'unset';
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    if (!isOpen || !service) return null;

    const details = service.details || {};

    const handleBook = () => {
        if (onBook) {
            onBook(service);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-fade-in-up flex flex-col">
                {/* Close Button - Sticky/Fixed in container */}
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 p-2 bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full text-white transition-colors z-20"
                    aria-label="Close"
                >
                    <X size={24} />
                </button>

                {/* Vertical Scroll Container */}
                <div className="overflow-y-auto flex-grow overscroll-contain">
                    {/* Hero Image */}
                    <div className="relative h-64 w-full shrink-0">
                        <img
                            src={service.image}
                            alt={service.name}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                            <div>
                                <span className="inline-block px-3 py-1 bg-zen-gold text-white text-xs font-bold rounded-full mb-2">
                                    {service.duration} • {service.price}
                                </span>
                                <h2 className="text-3xl font-display font-bold text-white leading-tight">
                                    {service.name}
                                </h2>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 md:p-8 space-y-8">
                        {/* Description */}
                        <div>
                            <h3 className="text-lg font-bold text-coffee-900 mb-3">About Theraphy</h3>
                            <p className="text-coffee-700 leading-relaxed">
                                {details.longDescription || service.description}
                            </p>
                        </div>

                        {/* Benefits */}
                        {details.benefits && (
                            <div>
                                <h3 className="text-lg font-bold text-coffee-900 mb-3">Key Benefits</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {details.benefits.map((benefit, index) => (
                                        <div key={index} className="flex items-start gap-2">
                                            <CheckCircle2 size={18} className="text-zen-gold mt-1 shrink-0" />
                                            <span className="text-coffee-700 text-sm">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Process */}
                        {details.process && (
                            <div className="bg-cream/30 p-6 rounded-xl border border-zen-gold/20">
                                <h3 className="text-lg font-bold text-coffee-900 mb-3 ml-1">The Process</h3>
                                {Array.isArray(details.process) ? (
                                    <ul className="space-y-3">
                                        {details.process.map((step, index) => (
                                            <li key={index} className="flex gap-3 text-sm text-coffee-800">
                                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-zen-gold/20 text-zen-gold font-bold text-xs shrink-0">
                                                    {index + 1}
                                                </span>
                                                <span>{step}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-coffee-700 text-sm">{details.process}</p>
                                )}
                            </div>
                        )}

                        {/* Recommended For */}
                        {details.recommendedFor && (
                            <div>
                                <h3 className="text-lg font-bold text-coffee-900 mb-3">Recommended For</h3>
                                <div className="flex flex-wrap gap-2">
                                    {details.recommendedFor.map((item, index) => (
                                        <span key={index} className="px-3 py-1 bg-gray-100 text-coffee-700 text-sm rounded-lg font-medium">
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* CTA */}
                        <div className="pt-4 mt-4 border-t border-gray-100">
                            <button
                                onClick={handleBook}
                                className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-premium hover:shadow-glow"
                            >
                                <Calendar size={20} />
                                Book This Therapy Now
                            </button>
                            <p className="text-center text-xs text-gray-500 mt-3">
                                Instant confirmation • Pay after service • Professional therapists
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceDetailModal;
